import styled from 'styled-components';
import { useAppSelector } from '@/hooks/useReduxHooks';
import TextButton from '@/components/common/TextButton';
import { useHistory } from 'react-router-dom';
import { routes } from '@/constant/routes.const';
import { IGlobalState } from '@/store';
import { REFUND_BOTH_TEXT, REFUND_ONLY_TEXT } from '@/constant/home.const';
import { IMG_BADGE_COINS, IMG_BADGE_LOCATION } from '@repo/assets';

type Props = {
  refund: number;
  refundAfter: number;
  isImmediateRefund: boolean;
  paymentStatus: string;
};

const Banner = ({
  refund,
  refundAfter,
  isImmediateRefund,
  paymentStatus,
}: Props) => {
  const { nationality } = useAppSelector((state) => state) as IGlobalState;
  const history = useHistory();
  const isPaymentComplete = paymentStatus === 'PAYMENT_COMPLETE';

  const getRefundAmount = () => {
    let result = refund + refundAfter;
    if (isImmediateRefund) {
      result = refund;
    }
    return result.toLocaleString();
  };

  const getRefundGuide = () => {
    let targetLanguage: 'KOR' | 'CHN' | 'USA' = 'USA';
    let targetStatus: 'PAYMENT_WAIT' | 'PAYMENT_COMPLETE' = 'PAYMENT_WAIT';

    if (nationality === 'KOR' || nationality === 'CHN') {
      targetLanguage = nationality;
    }

    if (isPaymentComplete) {
      targetStatus = paymentStatus;
    }

    if (isImmediateRefund) {
      const refundOnly = REFUND_ONLY_TEXT[targetLanguage][targetStatus];
      return {
        title: refundOnly.title,
        contents: refundOnly.contents,
      };
    }
    const refundBoth = REFUND_BOTH_TEXT[targetLanguage][targetStatus];
    return {
      title: refundBoth.title,
      contents: refundBoth.contents,
    };
  };

  return (
    <Wrapper>
      <RefundAmountWrapper>
        <MyRefund>
          {nationality === 'KOR'
            ? '내 환급금'
            : nationality === 'CHN'
              ? '我的退税额'
              : 'My Refund'}
        </MyRefund>
        <RefundAmountContainer>
          <RefundAmount>{getRefundAmount()}</RefundAmount>
          <Currency>KRW</Currency>
        </RefundAmountContainer>
      </RefundAmountWrapper>
      <GuideWrapper>
        <GuideTitle>{getRefundGuide().title}</GuideTitle>
        <GuideDetailWrapper>
          <GuideDetail>{getRefundGuide().contents}</GuideDetail>
          <ImgCoin
            src={isPaymentComplete ? IMG_BADGE_COINS : IMG_BADGE_LOCATION}
            alt=''
          />
        </GuideDetailWrapper>
        {!isImmediateRefund && (
          <ButtonWrapper>
            <TextButton
              title={
                nationality === 'KOR'
                  ? '영수증 보기'
                  : nationality === 'CHN'
                    ? '确认收据'
                    : 'View Tax refund Receipt'
              }
              onClick={() => history.push(routes.receipt)}
              color='#fff'
              width='100%'
            />
          </ButtonWrapper>
        )}
      </GuideWrapper>
    </Wrapper>
  );
};

export default Banner;

const Wrapper = styled.section`
  width: 100%;
  padding: 20px;
  box-shadow: 0px 0px 20px 0px rgba(174, 191, 204, 0.5);
  border-radius: 20px;
  background: #fff;
`;

const RefundAmountWrapper = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #a7c4fb;
  background: #f4f8ff;
`;

const MyRefund = styled.p`
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  color: #246cf6;
`;

const RefundAmountContainer = styled.p`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const RefundAmount = styled.span`
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  line-height: 150%;
  color: #246cf6;
`;

const Currency = styled.span`
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
  color: #246cf6;
`;

const GuideWrapper = styled.div`
  margin-top: 24px;
`;

const GuideTitle = styled.p`
  color: #1856cd;
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
`;

const GuideDetailWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
`;

const GuideDetail = styled.p`
  color: #3a3b3e;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
`;

const ImgCoin = styled.img`
  width: 60px;
  height: 60px;
`;

const ButtonWrapper = styled.div`
  @keyframes blink {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }

  margin-top: 24px;
  animation: blink 1500ms ease-in-out infinite;
`;
